import { type Actr } from '@opcentral/typr-lib';

/**
 * Session.ts - Session Model
 * 
 * CHANGE HISTORY:
 * - 251226_0852 [active] danielbeer: Remove @ts-nocheck and fix type errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
// import { generateId } from '@opcentral/core-lib';
const generateId = () => Math.random().toString(36).substring(2, 15);


interface SessionData {
  id?: string;
  userId?: string;
  type?: string;
  startTime?: string;
  endTime?: string | null;
  description?: string;
  project?: string | null;
  projectPath?: string | null;
  context?: Record<string, any>;
  activities?: any[];
  metadata?: Record<string, any>;
}

/**
 * Session class
 */
export class Session {
  id: string;
  userId: string;
  type: string;
  startTime: string;
  endTime: string | null;
  description: string;
  project: string | null;
  projectPath: string | null;
  context: Record<string, any>;
  activities: any[];
  metadata: Record<string, any>;

  constructor(data: SessionData = {}) {
    this.id = data.id || generateId();
    this.userId = data.userId || 'system';
    this.type = data.type || 'development';
    this.startTime = data.startTime || new Date().toISOString();
    this.endTime = data.endTime || null;
    this.description = data.description || '';
    this.project = data.project || null;
    this.projectPath = data.projectPath || null;
    this.context = data.context || {};
    this.activities = data.activities || [];
    this.metadata = data.metadata || {};
  }
  
  /**
   * Generate session slug
   * Format: [timestamp]_[userId]_[sessionId]_[description]
   */
  generateSlug(): string {
    const timestamp = this.startTime.replace(/[:.]/g, '-').split('T')[0];
    const shortId = this.id.substring(0, 8);
    const desc = this.description.toLowerCase().replace(/\s+/g, '-').substring(0, 30);
    return `${timestamp}_${this.userId}_${shortId}_${desc}`;
  }
  
  /**
   * Add activity
   */
  addActivity(activity: any): void {
    this.activities.push(activity);
  }
  
  /**
   * End session
   */
  end(): void {
    this.endTime = new Date().toISOString();
  }
  
  /**
   * Get session duration in milliseconds
   */
  getDuration(): number {
    if (!this.endTime) {
      return Date.now() - new Date(this.startTime).getTime();
    }
    return new Date(this.endTime).getTime() - new Date(this.startTime).getTime();
  }
  
  toJSON(): SessionData {
    return {
      id: this.id,
      userId: this.userId,
      type: this.type,
      startTime: this.startTime,
      endTime: this.endTime,
      description: this.description,
      project: this.project,
      projectPath: this.projectPath,
      context: this.context,
      activities: this.activities,
      metadata: this.metadata
    };
  }
  
  static fromJSON(json: SessionData): Session {
    return new Session(json);
  }
}

export default Session;
